/**
 * Map Poster Customizer - Dawn Theme Compatible
 * Supports multiple sections on the same page and add-on products
 */

/**
 * Live preview uses an embedded style with black (#000) road lines. Cart/static images fall back to
 * mapbox/light-v11 (soft gray roads). When that style is used, Static Images API `addlayer` draws
 * black roads from the same composite tile source so thumbnails match the customizer.
 */
const MAP_STATIC_LIGHT_V11_BEFORE_LAYER = 'road-label-simple';

const MAP_STATIC_LIGHT_V11_ROAD_DARKEN_LAYER = {
  id: 'map-poster-roads-dark',
  type: 'line',
  source: 'composite',
  'source-layer': 'road',
  minzoom: 5,
  filter: [
    'match',
    ['get', 'class'],
    'motorway',
    true,
    'trunk',
    true,
    'primary',
    true,
    'secondary',
    true,
    'tertiary',
    true,
    'street',
    true,
    'street_limited',
    true,
    'service',
    true,
    'track',
    true,
    'pedestrian',
    true,
    'path',
    true,
    false
  ],
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': '#000000',
    'line-width': [
      'interpolate',
      ['exponential', 1.5],
      ['zoom'],
      5,
      0.35,
      10,
      0.75,
      14,
      1.25,
      18,
      2.8,
      20,
      4.5
    ]
  }
};

class MapCustomizer {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId;
    const rawConfig = window.MapCustomizerConfig?.[this.sectionId] || {};
    this.config = { ...rawConfig };
    if (typeof this.config.mapboxToken === 'string') {
      this.config.mapboxToken = this.config.mapboxToken.trim();
    }
    if (typeof this.config.mapboxStyleUrl === 'string') {
      this.config.mapboxStyleUrl = this.config.mapboxStyleUrl.trim();
    }
    if (typeof this.config.staticMarkerIconUrl === 'string') {
      let u = this.config.staticMarkerIconUrl.trim();
      if (u.startsWith('//')) u = `https:${u}`;
      this.config.staticMarkerIconUrl = u;
    }
    
    // Map state
    this.map = null;
    this.marker = null;
    this.currentStyle = 'light';
    this.currentLocation = '';
    this.markerPosition = {
      lat: parseFloat(this.config.defaultLat) || 51.5074,
      lng: parseFloat(this.config.defaultLng) || -0.1278
    };
    
    // Marker color
    this.markerColor = '#d4a5a5';
    
    // Initialize
    this.init();
  }
  
  // Initialize size selection and sync visual state with radio button state
  initSizeSelection() {
    const sizeRadios = Array.from(this.$$('.size-option input[type="radio"]'));

    const syncSizeState = () => {
      // Clear all active classes first
      this.$$('.size-option').forEach(option => {
        option.classList.remove('active');
      });

      // Find which radio is actually checked
      let checkedRadio = null;
      for (const radio of sizeRadios) {
        if (radio.checked) {
          checkedRadio = radio;
          break;
        }
      }

      // If the checked radio is disabled (out of stock), switch to first enabled option
      if (checkedRadio && checkedRadio.disabled) {
        const firstEnabled = sizeRadios.find(r => !r.disabled);
        if (firstEnabled) {
          firstEnabled.checked = true;
          checkedRadio = firstEnabled;
        }
      }

      // If no radio is checked, default to first enabled one
      if (!checkedRadio && sizeRadios.length > 0) {
        const firstEnabled = sizeRadios.find(r => !r.disabled);
        checkedRadio = firstEnabled || sizeRadios[0];
        if (checkedRadio) checkedRadio.checked = true;
      }

      // Set active class only on checked radio's parent
      if (checkedRadio) {
        const sizeOption = checkedRadio.closest('.size-option');
        if (sizeOption) {
          sizeOption.classList.add('active');
        }
      }
    };

    // Run sync immediately
    syncSizeState();

    // Set up change event listeners for all radios
    sizeRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        syncSizeState();
        this.updateTotalPrice();
      });
    });

    // Also handle label clicks for better UX, respecting disabled state
    this.$$('.size-option').forEach(option => {
      option.addEventListener('click', (e) => {
        if (e.target.type === 'radio') return;

        const radio = option.querySelector('input[type="radio"]');
        if (radio && !radio.disabled && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Re-sync after a delay to catch browser form state restoration
    setTimeout(syncSizeState, 50);
    setTimeout(syncSizeState, 200);

    // Re-sync on pageshow event (handles back/forward navigation)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        setTimeout(syncSizeState, 0);
        setTimeout(syncSizeState, 100);
      }
    });
  }
  
  // Custom map style - clean architectural line-art
  get mapStyle() {
    return {
      version: 8,
      name: 'Light Line Art',
      sources: {
        'openmaptiles': {
          type: 'vector',
          url: 'mapbox://mapbox.mapbox-streets-v8'
        }
      },
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: { 'background-color': '#ffffff' }
        },
        {
          id: 'water',
          type: 'fill',
          source: 'openmaptiles',
          'source-layer': 'water',
          paint: { 'fill-color': '#e8e8e8' }
        },
        {
          id: 'landuse-park',
          type: 'fill',
          source: 'openmaptiles',
          'source-layer': 'landuse',
          filter: ['in', 'class', 'park', 'cemetery', 'glacier', 'pitch', 'sand'],
          paint: { 'fill-color': '#f5f5f5' }
        },
        {
          id: 'building',
          type: 'fill',
          source: 'openmaptiles',
          'source-layer': 'building',
          minzoom: 13,
          paint: {
            'fill-color': '#ffffff',
            'fill-outline-color': '#000000',
            'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.3, 15, 1]
          }
        },
        {
          id: 'road-path',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'road',
          minzoom: 14,
          filter: ['in', 'class', 'service', 'track', 'pedestrian', 'path'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 14, 0.2, 16, 0.4, 18, 0.8, 20, 1.5]
          }
        },
        {
          id: 'road-local',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'road',
          minzoom: 11,
          filter: ['in', 'class', 'street', 'street_limited'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 11, 0.2, 13, 0.4, 15, 0.6, 17, 1, 20, 2]
          }
        },
        {
          id: 'road-secondary',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'road',
          minzoom: 8,
          filter: ['in', 'class', 'secondary', 'tertiary'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 8, 0.2, 10, 0.4, 12, 0.6, 14, 0.9, 16, 1.4, 20, 3]
          }
        },
        {
          id: 'road-primary',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'road',
          minzoom: 6,
          filter: ['==', 'class', 'primary'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 6, 0.3, 8, 0.5, 10, 0.7, 12, 1, 14, 1.4, 16, 2, 20, 4]
          }
        },
        {
          id: 'road-highway',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'road',
          minzoom: 5,
          filter: ['in', 'class', 'motorway', 'trunk'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 5, 0.4, 8, 0.8, 10, 1.2, 12, 1.6, 14, 2, 16, 3, 20, 6]
          }
        },
        {
          id: 'railway',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'transit',
          minzoom: 10,
          filter: ['==', 'class', 'rail'],
          paint: {
            'line-color': '#000000',
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 14, 0.5, 18, 1],
            'line-dasharray': [3, 3]
          }
        },
        {
          id: 'admin-boundary',
          type: 'line',
          source: 'openmaptiles',
          'source-layer': 'admin',
          minzoom: 2,
          filter: ['>=', 'admin_level', 2],
          paint: {
            'line-color': '#cccccc',
            'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.5, 10, 1],
            'line-dasharray': [4, 2]
          }
        }
      ]
    };
  }
  
  // Convert Mapbox Studio editor URL to the style URL required by Mapbox GL JS.
  // Editor URL: https://console.mapbox.com/studio/styles/username/style-id/edit/...
  // Style URL:  mapbox://styles/username/style-id
  normalizeMapboxStyleUrl(input) {
    const url = input.trim();
    // Already a valid style URL
    if (url.startsWith('mapbox://styles/') || url.startsWith('https://api.mapbox.com/styles/v1/')) {
      return url;
    }
    // Mapbox Studio editor URL: extract username and style id
    const match = url.match(/studio\/styles\/([^/]+)\/([^/?]+)/);
    if (match) {
      const username = match[1];
      const styleId = match[2];
      return `mapbox://styles/${username}/${styleId}`;
    }
    return url;
  }
  
  // Get element within this section
  $(selector) {
    return this.section.querySelector(selector);
  }
  
  // Get all elements within this section
  $$(selector) {
    return this.section.querySelectorAll(selector);
  }
  
  init() {
    const token = this.config.mapboxToken || '';
    if (!token) {
      console.warn('MapCustomizer: No Mapbox token. Set it in Theme → customize → Map Customizer section, or Theme settings → Map poster cart preview.', this.sectionId);
      const loadingEl = this.$('.map-loading');
      if (loadingEl) {
        loadingEl.innerHTML =
          '<span style="color: #c00;">Add your Mapbox <strong>public</strong> token (pk.…) in Map Customizer section settings, or under Theme settings → Map poster cart preview.</span>';
      }
      return;
    }
    if (token.startsWith('sk.')) {
      console.error('MapCustomizer: Secret tokens (sk.) cannot be used in the browser. Create a public default token (pk.) in Mapbox.');
      const loadingEl = this.$('.map-loading');
      if (loadingEl) {
        loadingEl.innerHTML =
          '<span style="color: #c00;">Use a <strong>public</strong> Mapbox token (starts with pk.), not a secret (sk.) token.</span>';
      }
      return;
    }

    this.initMap();
    this.initEventListeners();
    this.initDeliveryNotice();
    
    // Update price and sync preview text on initial load after all listeners are set up
    // Use a delay to allow browser form state restoration to complete (especially when coming back from cart)
    setTimeout(() => {
      this.updateTotalPrice();
      this.syncPreviewFromInputs();
      this.syncLocationUppercaseButtonState();
    }, 100);
  }
  
  // Initialize frame selection and sync visual state with radio button state
  initFrameSelection() {
    // Find all frame radios
    const frameRadios = Array.from(this.$$('.frame-option input[type="radio"]'));
    
    // Sync function - ensures only one frame option has active class
    const syncFrameState = () => {
      // STEP 1: Clear ALL active classes first
      this.$$('.frame-option').forEach(option => {
        option.classList.remove('active');
      });
      
      // STEP 2: Find which radio is actually checked
      let checkedRadio = null;
      for (const radio of frameRadios) {
        if (radio.checked) {
          checkedRadio = radio;
          break;
        }
      }
      
      // If the checked radio is disabled (out of stock), switch to first enabled option
      if (checkedRadio && checkedRadio.disabled) {
        const firstEnabled = frameRadios.find(r => !r.disabled);
        if (firstEnabled) {
          firstEnabled.checked = true;
          checkedRadio = firstEnabled;
        }
      }
      
      // STEP 3: If no radio is checked, default to first enabled one (usually None)
      if (!checkedRadio && frameRadios.length > 0) {
        const firstEnabled = frameRadios.find(r => !r.disabled);
        checkedRadio = firstEnabled || frameRadios[0];
        if (checkedRadio) checkedRadio.checked = true;
      }
      
      // STEP 4: Set active class only on checked radio's parent
      if (checkedRadio) {
        const frameOption = checkedRadio.closest('.frame-option');
        if (frameOption) {
          frameOption.classList.add('active');
        }
      }

      // Keep the poster preview frame synced with the currently selected frame.
      // Note: selecting "White" uses a black outer frame (per client requirement).
      const frameValue = checkedRadio ? checkedRadio.value : '';
      this.syncPosterFramePreview(frameValue);
    };
    
    // Run sync immediately
    syncFrameState();
    
    // Set up change event listeners for all radios
    frameRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        syncFrameState();
        this.updateTotalPrice();
      });
    });
    
    // Also handle label clicks for better UX
    this.$$('.frame-option').forEach(option => {
      option.addEventListener('click', (e) => {
        // Don't double-trigger if clicking directly on the radio
        if (e.target.type === 'radio') return;
        
        const radio = option.querySelector('input[type="radio"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          // Trigger change event to update UI and price
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    
    // Re-sync after a delay to catch browser form state restoration
    setTimeout(syncFrameState, 50);
    setTimeout(syncFrameState, 200);
    
    // Re-sync on pageshow event (handles back/forward navigation)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was restored from cache
        setTimeout(syncFrameState, 0);
        setTimeout(syncFrameState, 100);
      }
    });
  }
  
  // Sync poster preview border/frame with frame selection.
  syncPosterFramePreview(frameValue) {
    const borderOuter = this.$('.poster-border-outer');
    if (!borderOuter) return;

    borderOuter.dataset.frame = 'none';

    if (frameValue === 'Black') {
      borderOuter.dataset.frame = 'black';
    } else if (frameValue === 'White') {
      borderOuter.dataset.frame = 'white';
    } else if (frameValue === 'Brown') {
      borderOuter.dataset.frame = 'brown';
    }
  }

  initDeliveryNotice() {
    const notice = this.$(`#delivery-notice-${this.sectionId}`);
    if (!notice) return;

    const dateEl = notice.querySelector('.delivery-notice-date');
    if (!dateEl) return;

    // Single dispatch date: 2 working days from today (weekends skipped).
    const dispatch = this.addWorkingDays(new Date(), 2);
    dateEl.textContent = this.formatDispatchDate(dispatch);
  }

  addWorkingDays(fromDate, workingDaysToAdd) {
    // Start counting from tomorrow (0 = today).
    const d = new Date(fromDate);
    d.setHours(0, 0, 0, 0);

    let added = 0;
    while (added < workingDaysToAdd) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay(); // 0 = Sun, 6 = Sat
      const isWeekend = day === 0 || day === 6;
      if (!isWeekend) added += 1;
    }

    return d;
  }

  formatDeliveryDate(date) {
    try {
      // Force English formatting for consistent client requirement.
      return new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).format(date);
    } catch (e) {
      // Fallback if Intl isn't available for some reason.
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    }
  }

  /** Dispatch pill: "Mon, Mar 30" style (matches shipping UI reference). */
  formatDispatchDate(date) {
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return this.formatDeliveryDate(date);
    }
  }

  // Sync preview text with input values (handles browser form state restoration)
  syncPreviewFromInputs() {
    const titleInput = this.$('.poster-title-input');
    const namesInput = this.$('.poster-names-input');
    const dateInput = this.$('.poster-date-input');
    
    // Update preview text from input values
    if (titleInput) {
      this.updatePreviewText('title', titleInput.value);
    }
    if (namesInput) {
      this.updatePreviewText('names', namesInput.value);
    }
    if (dateInput) {
      this.updatePreviewText('date', dateInput.value);
    }
  }
  
  initMap() {
    const mapContainer = this.$('.map-container');
    if (!mapContainer) return;
    
    // Set Mapbox access token
    mapboxgl.accessToken = this.config.mapboxToken;
    
    const loadingEl = this.$('.map-loading');
    
    // Use Mapbox Studio style URL if set (auto-updates when customer changes style in Mapbox Studio);
    // otherwise use the built-in style. Convert editor URL to style URL if user pasted the Studio edit link.
    let styleOption = this.mapStyle;
    const rawStyleUrl = this.config.mapboxStyleUrl && this.config.mapboxStyleUrl.trim();
    if (rawStyleUrl) {
      styleOption = this.normalizeMapboxStyleUrl(rawStyleUrl);
    }
    
    // Initialize Mapbox map
    try {
      this.map = new mapboxgl.Map({
        container: mapContainer,
        style: styleOption,
        center: [this.markerPosition.lng, this.markerPosition.lat],
        zoom: this.config.defaultZoom || 14,
        attributionControl: false,
        logoPosition: 'bottom-left'
      });
    } catch (err) {
      console.error('MapCustomizer: failed to create map', err);
      if (loadingEl) {
        loadingEl.style.display = 'flex';
        loadingEl.innerHTML =
          '<span style="color: #c00;">Map could not start. Check the token and Mapbox style URL, then refresh.</span>';
      }
      return;
    }

    this.map.on('error', (e) => {
      const err = e && e.error ? e.error : e;
      console.error('MapCustomizer: Mapbox error', err);
      const msg =
        err && err.message
          ? err.message
          : 'Map failed to load (often an invalid token, URL restrictions on the token, or blocked network).';
      if (loadingEl) {
        loadingEl.style.display = 'flex';
        loadingEl.innerHTML = `<span style="color: #c00;">${String(msg).replace(/</g, '&lt;')}</span>`;
      }
    });

    // On map load
    this.map.on('load', () => {
      if (loadingEl) {
        loadingEl.style.display = 'none';
      }
      
      // Hide static marker fallback
      const staticMarker = this.$('.custom-marker');
      if (staticMarker) {
        staticMarker.style.display = 'none';
      }
    });
    
    // Create draggable marker
    const markerEl = this.createMarkerElement();
    this.marker = new mapboxgl.Marker({
      element: markerEl,
      draggable: true,
      anchor: 'center'
    })
      .setLngLat([this.markerPosition.lng, this.markerPosition.lat])
      .addTo(this.map);
    
    // Handle marker drag
    this.marker.on('dragend', () => {
      const lngLat = this.marker.getLngLat();
      this.markerPosition = { lat: lngLat.lat, lng: lngLat.lng };
      this.updateCoordinatesDisplay(lngLat.lat, lngLat.lng);
      this.updateCoordinateInputs(lngLat.lat, lngLat.lng);
      this.reverseGeocode(lngLat.lng, lngLat.lat);
    });
    
    // Update zoom level on change
    this.map.on('zoomend', this.debounce(() => this.updateZoomLevel(), 100));
    
    // Set initial coordinates
    this.updateCoordinatesDisplay(this.markerPosition.lat, this.markerPosition.lng);
    this.updateCoordinateInputs(this.markerPosition.lat, this.markerPosition.lng);
  }
  
  createMarkerElement() {
    const el = document.createElement('div');
    el.className = 'mapbox-heart-marker';
    el.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="${this.markerColor}" stroke="#000000" stroke-width="1" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2)); cursor: grab;">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
    `;
    el.style.cursor = 'grab';
    return el;
  }
  
  initEventListeners() {
    // Size selection - initialize and sync state (includes label click handlers)
    this.initSizeSelection();
    
    // Digital delivery checkbox
    const digitalCheckbox = this.$('.digital-delivery-checkbox');
    if (digitalCheckbox) {
      digitalCheckbox.addEventListener('change', () => {
        const option = digitalCheckbox.closest('.checkbox-option');
        if (option) {
          option.classList.toggle('checked', digitalCheckbox.checked);
        }
        this.updateTotalPrice();
      });
    }
    
    // Frame selection - initialize and sync state (includes label click handlers)
    this.initFrameSelection();
    
    // Gallery thumbnails
    this.$$('.gallery-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.$$('.gallery-thumb').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        this.switchPreviewView(thumb.dataset.view, thumb.dataset.imageUrl);
      });
    });
    
    // Location search
    const searchInput = this.$('.location-search');
    const searchBtn = this.$('.search-btn');
    
    if (searchInput) {
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.searchLocation(searchInput.value);
        }
      });
    }
    
    if (searchBtn && searchInput) {
      searchBtn.addEventListener('click', () => {
        if (searchInput.value) {
          this.searchLocation(searchInput.value);
        }
      });
    }
    
    // Apply coordinates button
    const applyCoordsBtn = this.$('.apply-coords-btn');
    if (applyCoordsBtn) {
      applyCoordsBtn.addEventListener('click', () => this.applyManualCoordinates());
    }
    
    // Text inputs - live preview
    const titleInput = this.$('.poster-title-input');
    const namesInput = this.$('.poster-names-input');
    const dateInput = this.$('.poster-date-input');
    
    // Add input listeners for live preview updates
    if (titleInput) {
      titleInput.addEventListener('input', () => {
        this.updatePreviewText('title', titleInput.value);
        this.syncLocationUppercaseButtonState();
      });
    }
    if (namesInput) {
      namesInput.addEventListener('input', () => this.updatePreviewText('names', namesInput.value));
    }
    const titleInputForUppercase = this.$('.poster-title-input');
    const locationUppercaseBtn = this.$('.location-uppercase-btn');
    if (locationUppercaseBtn && titleInputForUppercase) {
      locationUppercaseBtn.addEventListener('click', () => {
        const val = titleInputForUppercase.value;
        const isUppercase = val.length > 0 && val === val.toUpperCase();
        titleInputForUppercase.value = isUppercase ? val.toLowerCase() : val.toUpperCase();
        this.updatePreviewText('title', titleInputForUppercase.value);
        this.syncLocationUppercaseButtonState();
      });
    }
    if (dateInput) {
      dateInput.addEventListener('input', () => this.updatePreviewText('date', dateInput.value));
    }
    // Note: Initial sync happens in syncPreviewFromInputs() after page load
    
    // Form submission
    const form = this.$('.map-product-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }
  }
  
  switchPreviewView(view, imageUrl = null) {
    const posterCanvas = this.$('.poster-canvas');
    const productImageView = this.$('.product-image-view');
    const previewHint = this.$('.preview-hint');
    
    if (view === 'map') {
      if (posterCanvas) posterCanvas.style.display = 'flex';
      if (productImageView) productImageView.style.display = 'none';
      if (previewHint) previewHint.style.display = 'flex';
      if (this.map) {
        setTimeout(() => this.map.resize(), 100);
      }
    } else if (view === 'image' && imageUrl) {
      if (posterCanvas) posterCanvas.style.display = 'none';
      if (productImageView) productImageView.style.display = 'flex';
      if (previewHint) previewHint.style.display = 'none';
      
      const img = this.$('.product-image-display');
      if (img) {
        img.src = imageUrl;
      }
    }
  }
  
  async searchLocation(query) {
    if (!query) return;
    
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${this.config.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        
        this.map.flyTo({
          center: [lng, lat],
          zoom: 14,
          duration: 1500
        });
        
        if (this.marker) {
          this.marker.setLngLat([lng, lat]);
          this.markerPosition = { lat, lng };
        }
        
        this.currentLocation = feature.place_name;
        this.updateCoordinateInputs(lat, lng);
        this.updateCoordinatesDisplay(lat, lng);
      } else {
        alert('Location not found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error searching for location. Please try again.');
    }
  }
  
  async reverseGeocode(lng, lat) {
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${this.config.mapboxToken}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        this.currentLocation = data.features[0].place_name;
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  }
  
  applyManualCoordinates() {
    const latInput = this.$('.latitude-input');
    const lngInput = this.$('.longitude-input');
    
    if (!latInput || !lngInput) return;
    
    const lat = parseFloat(latInput.value);
    const lng = parseFloat(lngInput.value);
    
    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid coordinates.');
      return;
    }
    
    if (lat < -90 || lat > 90) {
      alert('Latitude must be between -90 and 90.');
      return;
    }
    
    if (lng < -180 || lng > 180) {
      alert('Longitude must be between -180 and 180.');
      return;
    }
    
    this.map.flyTo({
      center: [lng, lat],
      zoom: 14,
      duration: 1500
    });
    
    if (this.marker) {
      this.marker.setLngLat([lng, lat]);
      this.markerPosition = { lat, lng };
    }
    
    this.updateCoordinatesDisplay(lat, lng);
    this.reverseGeocode(lng, lat);
  }
  
  updateCoordinateInputs(lat, lng) {
    const latInput = this.$('.latitude-input');
    const lngInput = this.$('.longitude-input');
    
    if (latInput) latInput.value = lat.toFixed(6);
    if (lngInput) lngInput.value = lng.toFixed(6);
  }
  
  updateCoordinatesDisplay(lat, lng) {
    const coordsEl = this.$('.preview-coordinates');
    if (!coordsEl) return;
    
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    
    coordsEl.textContent = `${Math.abs(lat).toFixed(4)}° ${latDir}, ${Math.abs(lng).toFixed(4)}° ${lngDir}`;
  }
  
  // Location title: show as typed (uppercase/lowercase controlled by the Aa button only)
  formatLocationTitle(value) {
    const raw = value && value.trim() ? value.trim() : '';
    return raw || 'LOCATION';
  }

  /**
   * Mapbox Static Images API path (owner/styleId) for cart preview.
   * When no Studio style is set, the live map uses embedded JSON — we approximate with Mapbox light-v11.
   */
  parseStylePathForStaticApi(input) {
    if (!input || !String(input).trim()) return null;
    const normalized = this.normalizeMapboxStyleUrl(String(input).trim());
    if (normalized.startsWith('mapbox://styles/')) {
      const rest = normalized.replace('mapbox://styles/', '');
      const slash = rest.indexOf('/');
      if (slash === -1) return null;
      return { owner: rest.slice(0, slash), id: rest.slice(slash + 1) };
    }
    if (normalized.startsWith('https://api.mapbox.com/styles/v1/')) {
      try {
        const u = new URL(normalized);
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts[0] === 'styles' && parts[1] === 'v1' && parts.length >= 4) {
          return { owner: parts[2], id: parts[3] };
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Mapbox Static Images API style path (owner/styleId), same as live preview / cart thumbnail.
   * Built-in embedded style → mapbox/light-v11 (+ Static API addlayer for black roads); Studio URL → parsed username/style-id.
   */
  getStaticStylePath() {
    let stylePath = 'mapbox/light-v11';
    const rawStyleUrl = this.config.mapboxStyleUrl && this.config.mapboxStyleUrl.trim();
    if (rawStyleUrl) {
      const parsed = this.parseStylePathForStaticApi(rawStyleUrl);
      if (parsed) {
        stylePath = `${parsed.owner}/${parsed.id}`;
      }
    }
    return stylePath;
  }

  /**
   * Short Mapbox static image URL for cart / order previews (line item property).
   * Uses the same Studio style when configured; otherwise mapbox/light-v11 (close to the built-in line-art look).
   */
  buildMapStaticPreviewUrl() {
    const token = this.config.mapboxToken;
    if (!token) return '';

    let lat = this.markerPosition.lat;
    let lng = this.markerPosition.lng;
    if (this.marker) {
      const ll = this.marker.getLngLat();
      lat = ll.lat;
      lng = ll.lng;
    }

    const zoomRaw = this.map ? this.map.getZoom() : (this.config.defaultZoom || 14);
    const zoom = Math.max(0, Math.min(22, Math.round(zoomRaw * 10) / 10));

    const stylePath = this.getStaticStylePath();

    const w = 240;
    const h = 320;
    const position = `${lng},${lat},${zoom}`;
    // Same heart asset as the live customizer (Static API custom marker; PNG hosted on theme CDN).
    const iconUrl = this.config.staticMarkerIconUrl || '';
    const overlay = iconUrl
      ? `url-${encodeURIComponent(iconUrl)}(${lng},${lat})`
      : `pin-s-heart+${String(this.markerColor || '#d4a5a5').replace(/^#/, '')}(${lng},${lat})`;
    const base = `https://api.mapbox.com/styles/v1/${stylePath}/static/${overlay}/${position}/${w}x${h}@2x`;
    const params = new URLSearchParams({
      access_token: token,
      attribution: 'false',
      logo: 'false'
    });
    if (stylePath === 'mapbox/light-v11') {
      params.append('addlayer', JSON.stringify(MAP_STATIC_LIGHT_V11_ROAD_DARKEN_LAYER));
      params.append('before_layer', MAP_STATIC_LIGHT_V11_BEFORE_LAYER);
    }
    return `${base}?${params.toString()}`;
  }

  syncLocationUppercaseButtonState() {
    const titleInput = this.$('.poster-title-input');
    const btn = this.$('.location-uppercase-btn');
    if (!btn || !titleInput) return;
    const val = titleInput.value;
    const isUppercase = val.length > 0 && val === val.toUpperCase();
    btn.classList.toggle('active', isUppercase);
    btn.setAttribute('aria-pressed', isUppercase ? 'true' : 'false');
  }

  updatePreviewText(field, value) {
    if (field === 'title') {
      const titleEl = this.$('.preview-title');
      if (titleEl) {
        titleEl.textContent = this.formatLocationTitle(value);
      }
    } else if (field === 'names') {
      const namesEl = this.$('.preview-names');
      if (namesEl) {
        namesEl.textContent = value || 'Your Names';
      }
    } else if (field === 'date') {
      const dateEl = this.$('.preview-date');
      if (dateEl) {
        dateEl.textContent = value || 'Your Special Date';
      }
    }
  }
  
  updateTotalPrice() {
    // All prices are stored in cents for consistency
    const selectedVariant = this.$('.size-option input[type="radio"]:checked') || this.$('.size-option.active input');
    let totalCents = selectedVariant ? parseInt(selectedVariant.dataset.price) || 0 : 0;
    
    // Add digital delivery (price is in cents)
    const digitalCheckbox = this.$('.digital-delivery-checkbox');
    if (digitalCheckbox && digitalCheckbox.checked) {
      const digitalPrice = parseInt(digitalCheckbox.dataset.price) || 0;
      totalCents += digitalPrice;
    }
    
    // Add frame (price is in cents) - use :checked selector to get the actually checked radio
    const selectedFrame = this.$('.frame-option input[type="radio"]:checked');
    if (selectedFrame && selectedFrame.dataset.price) {
      const framePrice = parseInt(selectedFrame.dataset.price) || 0;
      totalCents += framePrice;
    }
    
    // Format and display (convert cents to store currency)
    const priceDisplay = this.$('.current-price');
    if (priceDisplay) {
      // Get currency from config (fallback to EUR if not set)
      const currency = this.config.currencyCode || 'EUR';
      const currencySymbol = this.config.currencySymbol || '€';
      
      // Format price using store currency
      const price = totalCents / 100;
      
      // Try to format with Intl.NumberFormat if currency code is available
      try {
        const formatted = price.toLocaleString(undefined, {
          style: 'currency',
          currency: currency
        });
        priceDisplay.textContent = formatted;
      } catch (e) {
        // Fallback: use symbol and manual formatting
        priceDisplay.textContent = currencySymbol + price.toFixed(2);
      }
    }
  }
  
  updateZoomLevel() {
    // Store zoom level for cart properties (one decimal place, e.g. 10.2, 11.7)
    this.currentZoom = this.map ? Math.round(this.map.getZoom() * 10) / 10 : 14;
  }
  
  validateForm() {
    const titleInput = this.$('.poster-title-input');
    const namesInput = this.$('.poster-names-input');
    const dateInput = this.$('.poster-date-input');
    
    if (!titleInput?.value?.trim()) {
      alert('Please enter a location title for your poster (e.g., DUBLIN).');
      titleInput?.focus();
      return false;
    }
    
    if (!namesInput?.value?.trim()) {
      alert('Please enter the names for your poster.');
      namesInput?.focus();
      return false;
    }
    
    if (!dateInput?.value?.trim()) {
      alert('Please enter a date for your poster.');
      dateInput?.focus();
      return false;
    }
    
    return true;
  }
  
  getLineItemProperties(includeAddons = false) {
    const titleInput = this.$('.poster-title-input');
    const namesInput = this.$('.poster-names-input');
    const dateInput = this.$('.poster-date-input');
    const searchInput = this.$('.location-search');
    
    // Get current marker position
    let lat = this.markerPosition.lat;
    let lng = this.markerPosition.lng;
    
    if (this.marker) {
      const lngLat = this.marker.getLngLat();
      lat = lngLat.lat;
      lng = lngLat.lng;
    }
    
    // Customer-facing cart/checkout hides anything starting with `_`.
    // We keep all print/order-relevant fields, but only show the minimum to the customer.
    const properties = {
      '_Map Style': this.currentStyle,
      // Visible so the customer can confirm the exact location selected in the customizer.
      'Location': this.currentLocation || searchInput?.value || 'Custom Location',
      '_Latitude': lat.toFixed(6),
      '_Longitude': lng.toFixed(6),
      '_Zoom Level': this.map ? (Math.round(this.map.getZoom() * 10) / 10).toString() : '14',
      '_Location Title': this.formatLocationTitle(titleInput?.value || ''),
      '_Names': namesInput?.value || '',
      '_Date': dateInput?.value || ''
    };

    // Provide generator-critical keys without `_` so the checkout/order
    // map preview image can reliably match the customer's selection.
    // (Some checkout/order renderers look specifically for these exact keys.)
    properties['Map Style'] = this.currentStyle;
    properties['Latitude'] = lat.toFixed(6);
    properties['Longitude'] = lng.toFixed(6);
    properties['Zoom Level'] = this.map ? (Math.round(this.map.getZoom() * 10) / 10).toString() : '14';
    properties['Location Title'] = this.formatLocationTitle(titleInput?.value || '');
    properties['Names'] = namesInput?.value || '';
    properties['Date'] = dateInput?.value || '';

    // Cart/checkout thumbnail: same Mapbox style as live preview (hidden from customer).
    properties['_Static Style Path'] = this.getStaticStylePath();
    
    // If add-on products aren't configured, include add-ons as properties
    if (includeAddons) {
      const digitalCheckbox = this.$('.digital-delivery-checkbox');
      if (digitalCheckbox && digitalCheckbox.checked) {
        const hasProduct = digitalCheckbox.dataset.variantId && digitalCheckbox.dataset.variantId !== '';
        if (!hasProduct) {
          properties['_Digital Delivery'] = 'Yes';
        }
      }
      
      const selectedFrame = this.$('.frame-option.active input');
      if (selectedFrame && selectedFrame.value) {
        const hasProduct = selectedFrame.dataset.variantId && selectedFrame.dataset.variantId !== '';
        if (!hasProduct) {
          // Visible in cart/checkout.
          properties['Frame Color'] = selectedFrame.value;
          // Also keep a hidden copy for any downstream generator that still expects the old key name.
          properties['_Frame'] = selectedFrame.value;
        }
      }
    }

    const previewUrl = this.buildMapStaticPreviewUrl();
    if (previewUrl) {
      properties['_Map Preview URL'] = previewUrl;
    }

    // Cart/checkout: show frame around map thumbnail even when frame is a separate line item (add-on product).
    const posterFrameRadio =
      this.$('.frame-option input[type="radio"]:checked') || this.$('.frame-option.active input[type="radio"]');
    if (posterFrameRadio && posterFrameRadio.value) {
      properties['_Poster Frame'] = posterFrameRadio.value;
    }
    
    return properties;
  }
  
  async handleAddToCart() {
    if (!this.validateForm()) return;
    
    const addToCartBtn = this.$('.add-to-cart-btn');
    const btnText = this.$('.btn-text');
    const btnLoading = this.$('.btn-loading');
    const btnIcon = this.$('.btn-icon');
    
    // Show loading state
    if (addToCartBtn) addToCartBtn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnIcon) btnIcon.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline-flex';
    
    try {
      // Build items array for cart
      const items = [];
      
      // Get selected variant (main product)
      const selectedVariant = this.$('.size-option input[type="radio"]:checked') || this.$('.size-option.active input');
      if (!selectedVariant) {
        throw new Error('Please select a size.');
      }
      
      // Check if add-on products are configured
      const digitalCheckbox = this.$('.digital-delivery-checkbox');
      // Use :checked selector to get the actually checked radio button, not just the one with active class
      const selectedFrame = this.$('.frame-option input[type="radio"]:checked') || this.$('.frame-option.active input[type="radio"]');
      
      // Check if variant IDs exist and are valid
      const digitalVariantId = digitalCheckbox?.dataset.variantId;
      const frameVariantId = selectedFrame?.dataset.variantId;
      
      const hasDigitalProduct = digitalCheckbox && 
                                digitalCheckbox.checked && 
                                digitalVariantId && 
                                digitalVariantId !== '' && 
                                !isNaN(parseInt(digitalVariantId));
      
      const hasFrameProduct = selectedFrame && 
                              selectedFrame.value && 
                              frameVariantId && 
                              frameVariantId !== '' && 
                              !isNaN(parseInt(frameVariantId));
      
      // Warn if add-ons are selected but products aren't configured
      if (digitalCheckbox?.checked && !hasDigitalProduct) {
        console.warn('⚠️ Digital delivery selected but no product configured. Add-on will be stored as property only. Cart total will NOT include this price.');
        console.warn('💡 To fix: Configure "Digital Delivery Product" in Theme Customizer → Map Customizer section settings.');
      }
      if (selectedFrame?.value && !hasFrameProduct) {
        console.warn(`⚠️ Frame "${selectedFrame.value}" selected but no product configured. Add-on will be stored as property only. Cart total will NOT include this price.`);
        console.warn(`💡 To fix: Configure "${selectedFrame.value} Frame Product" in Theme Customizer → Map Customizer section settings.`);
      }
      
      // Determine if we need to include add-ons as line item properties
      const includeAddonsAsProperties = (digitalCheckbox?.checked && !hasDigitalProduct) || 
                                         (selectedFrame?.value && !hasFrameProduct);
      
      // Main product with line item properties
      items.push({
        id: parseInt(selectedVariant.value),
        quantity: 1,
        properties: this.getLineItemProperties(includeAddonsAsProperties)
      });
      
      // Add digital delivery as separate product if configured
      if (hasDigitalProduct) {
        const digitalVariantIdInt = parseInt(digitalVariantId);
        console.log('Adding digital delivery product:', digitalVariantIdInt);
        items.push({
          id: digitalVariantIdInt,
          quantity: 1,
          properties: {
            '_Associated Product': this.config.productTitle || 'Map Poster',
            '_Location Title': this.formatLocationTitle(this.$('.poster-title-input')?.value || '')
          }
        });
      }
      
      // Add frame as separate product if configured
      if (hasFrameProduct) {
        const frameVariantIdInt = parseInt(frameVariantId);
        console.log('Adding frame product:', frameVariantIdInt, selectedFrame.value);
        items.push({
          id: frameVariantIdInt,
          quantity: 1,
          properties: {
            '_Associated Product': this.config.productTitle || 'Map Poster',
            'Frame Color': selectedFrame.value,
            '_Location Title': this.formatLocationTitle(this.$('.poster-title-input')?.value || '')
          }
        });
      }
      
      console.log('Cart items to add:', items);
      
      // Add items to cart using Shopify AJAX API
      const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
      }
      
      const result = await response.json();
      console.log('Added to cart:', result);
      
      // Success - redirect to cart or show notification
      // Option 1: Redirect to cart
      window.location.href = this.config.cartUrl || '/cart';
      
      // Option 2: If you want to use Dawn's cart drawer instead, dispatch event:
      // document.dispatchEvent(new CustomEvent('cart:refresh'));
      // this.showSuccessMessage();
      
    } catch (error) {
      console.error('Add to cart error:', error);
      alert(error.message || 'Failed to add to cart. Please try again.');
      
      // Reset button state
      if (addToCartBtn) addToCartBtn.disabled = false;
      if (btnText) btnText.style.display = 'inline';
      if (btnIcon) btnIcon.style.display = 'inline-flex';
      if (btnLoading) btnLoading.style.display = 'none';
    }
  }
  
  showSuccessMessage() {
    // Optional: Show a success message instead of redirecting
    const btn = this.$('.add-to-cart-btn');
    const btnText = this.$('.btn-text');
    const btnLoading = this.$('.btn-loading');
    const btnIcon = this.$('.btn-icon');
    
    if (btn) btn.disabled = false;
    if (btnLoading) btnLoading.style.display = 'none';
    if (btnIcon) btnIcon.style.display = 'inline-flex';
    
    if (btnText) {
      const originalText = btnText.textContent;
      btnText.textContent = 'Added to Cart!';
      btnText.style.display = 'inline';
      
      setTimeout(() => {
        btnText.textContent = originalText;
      }, 2000);
    }
  }
  
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize all map customizer sections on the page
function initMapCustomizers() {
  document.querySelectorAll('.map-customizer').forEach(section => {
    // Check if already initialized
    if (!section.dataset.initialized) {
      section.dataset.initialized = 'true';
      new MapCustomizer(section);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMapCustomizers);
} else {
  initMapCustomizers();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.map-customizer');
  if (section && !section.dataset.initialized) {
    section.dataset.initialized = 'true';
    new MapCustomizer(section);
  }
});
